import Image from 'next/image';
import TextLink from 'components/TextLink/TextLink';
import { WeatherForecast } from 'types/forecastAPI';
import { useInView } from 'react-intersection-observer';
import useIsWindowSize from 'utils/hooks/useIsWindowSize';
import BuienradarImageLoader from 'utils/imageLoader';
import { getAltTextWeatherIcon } from 'utils/getAltTextHelper';
import { colors } from '../../../styles/theme';
import ArrowIcon from '../../../../public/icons/arrow.svg';
import { EnumMyWeatherLinkTypes, myWeatherLink } from './MyWeather';

import cssVariables from 'styles/variables.module.scss';
import styles from './Forecast.module.scss';
import { GeoLocation } from 'types/locationAPI';

const getDay = (date, i) => {
	if (i === 0) return 'vandaag';
	if (i === 1) return 'morgen';

	return new Date(date).toLocaleDateString('nl-NL', { weekday: 'long' });
};

const getFormattedTemperature = (val: number) => {
	if (Object.is(val, -0)) return '0°';
	return `${val.toFixed(0)}°`;
};

function scrollTo(direction: 'left' | 'right') {
	const forecastContainer = document.querySelector<HTMLElement>(
		`.${styles.forecastDaysContainer}`
	);
	if (forecastContainer) {
		if (direction === 'left') {
			forecastContainer.scrollLeft -= 120;
		}
		if (direction === 'right') {
			forecastContainer.scrollLeft += 120;
		}
	}
}

export function Forecast({
	forecastData,
	currentLocation,
}: {
	forecastData: WeatherForecast;
	currentLocation: GeoLocation;
}) {
	const isLocationNlorBe =
		currentLocation.countrycode === 'NL' ||
		currentLocation.countrycode === 'BE';

	const isDesktop = useIsWindowSize({ mediaQuery: cssVariables.desktopS });
	const imageDimensions = {
		width: isDesktop ? 30 : 50,
		height: isDesktop ? 30 : 50,
	};

	const { ref: firstDayElement, inView: firstDayElementInView } = useInView({
		threshold: 0.8,
	});
	const { ref: lastDayElement, inView: lastDayElementInView } = useInView({
		threshold: 0.8,
	});
	if (!forecastData || !forecastData.days || !currentLocation) return null;
	const linkToday = myWeatherLink({
		currentLocation,
		type: EnumMyWeatherLinkTypes.Today,
	});
	const link14days = myWeatherLink({
		currentLocation,
		type: EnumMyWeatherLinkTypes['14day'],
	});
	const link5days = myWeatherLink({
		currentLocation,
		type: EnumMyWeatherLinkTypes['5day'],
	});
	let amountToRender = 5;
	if (isLocationNlorBe && isDesktop) {
		amountToRender = 3;
	} else if (!isLocationNlorBe && isDesktop) {
		amountToRender = 6;
	}

	return (
		<div className={styles.forecastContainer} data-cy="forecast">
			<div className={styles.forecastWrapper}>
				<button
					type="button"
					className={`${styles.forecastButton} ${styles.forecastButtonLeft}`}
					style={{
						visibility: firstDayElementInView
							? 'hidden'
							: 'initial',
					}}
					onClick={() => scrollTo('left')}
				>
					<ArrowIcon />
				</button>
				<button
					type="button"
					className={`${styles.forecastButton} ${styles.forecastButtonRight}`}
					style={{
						visibility: lastDayElementInView ? 'hidden' : 'initial',
					}}
					onClick={() => scrollTo('right')}
				>
					<ArrowIcon />
				</button>
				<div className={styles.forecastDaysContainer}>
					{forecastData.days
						.slice(0, amountToRender)
						.map((day, i) => {
							const href = i === 0 ? linkToday : link14days;
							return (
								<a
									key={day.datetime}
									href={href}
									className={styles.forecastItem}
									ref={i === 0 ? firstDayElement : null}
									data-day={i}
								>
									<span className={styles.forecastItemDay}>
										{getDay(day.date, i)}
									</span>
									<span>
										<Image
											loading="lazy"
											loader={BuienradarImageLoader}
											alt={
												day.iconcode
													? getAltTextWeatherIcon(
															day.iconcode
													  )
													: ''
											}
											height={imageDimensions.height}
											width={imageDimensions.width}
											src={`https://cdn.buienradar.nl/resources/images/icons/weather/116x116/${day.iconcode}.png`}
										/>
									</span>
									{day.mintemp
										? `${getFormattedTemperature(
												day.mintemp
										  )}/ `
										: null}
									{day.maxtemp
										? getFormattedTemperature(day.maxtemp)
										: null}
									<span>
										{day.winddirection} {day.beaufort}
									</span>
								</a>
							);
						})}
					<a
						href={link14days}
						className={`${styles.forecastItem} ${styles.forecastItemEnd}`}
						ref={lastDayElement}
					>
						Naar
						<br />
						14-daagse
					</a>
				</div>
				<div className={styles.forecastDaysBullets}>
					<span
						className={styles.forecastDaysBullet}
						style={
							{
								'--backgroundColor': firstDayElementInView
									? colors.waterBlue
									: '',
							} as React.CSSProperties
						}
					></span>
					<span
						className={styles.forecastDaysBullet}
						style={
							{
								'--backgroundColor':
									!firstDayElementInView &&
									!lastDayElementInView
										? colors.waterBlue
										: '',
							} as React.CSSProperties
						}
					></span>
					<span
						className={styles.forecastDaysBullet}
						style={
							{
								'--backgroundColor': lastDayElementInView
									? colors.waterBlue
									: '',
							} as React.CSSProperties
						}
					></span>
				</div>
			</div>
			{isDesktop ? (
				<TextLink appearance="text" href={link14days}>
					14-daagse verwachting
				</TextLink>
			) : (
				<>
					<TextLink appearance="text" href={link5days}>
						5-daagse verwachting per uur
					</TextLink>
					<TextLink appearance="text" href={link14days}>
						14-daagse verwachting
					</TextLink>
				</>
			)}
		</div>
	);
}
